import { LocaleContext } from '../../components/LocaleContext';
import {
    buildPreviewPeriodOptionLabels,
    registerPreviewCalendarLocale,
} from '../../utils/previewUiLocale';
import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { DatabaseContext } from "../../redux/DatabaseStore";
import { getRequest } from "../../api/ApiController";
import { getModuleSettingValue } from "../../utils/moduleSettings";
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css'
import { RefreshIcon } from "../svg/svgIcons";
import UtilContext from "../../components/UtilContext";
import { Chart } from 'primereact/chart';
import { Calendar } from '../../components/PrimeCalendar';
import { Skeleton } from 'primereact/skeleton';
import { addLocale } from 'primereact/api';
import {
    PREVIEW_PERIOD_OPTIONS as PERIOD_OPTIONS,
    PREVIEW_PERIOD_DEFAULT,
    defaultDateForPeriod,
    datePickerPropsForPeriod,
    toLocalDateString,
} from '../../utils/previewPeriodSelection';

const PREVIEW_PAGE = 'PreviewCal';

function parseSavedPeriod(attrs) {
    if (!attrs?.preview_period || typeof attrs.preview_period?.timePeriod !== 'string') return null;
    if (!PERIOD_OPTIONS.includes(attrs.preview_period.timePeriod)) return null;
    const { timePeriod } = attrs.preview_period;
    const date = attrs.preview_period.date;
    if (timePeriod === 'Všetko') return { timePeriod, date: null };
    if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
        return { timePeriod, date: date.map((d) => (d != null ? new Date(d) : null)) };
    }
    if (date != null) {
        const d = new Date(date);
        if (!isNaN(d.getTime())) return { timePeriod, date: d };
    }
    return null;
}

function serializePeriod(timePeriod, date) {
    const payload = { timePeriod };
    if (date == null) payload.date = null;
    else if (Array.isArray(date)) payload.date = date.map((d) => (d instanceof Date ? toLocalDateString(d) : d));
    else payload.date = date instanceof Date ? toLocalDateString(date) : date;
    return payload;
}

function getDateRange(timePeriod, date) {
    if (!date || timePeriod === 'Všetko') return null;
    if (Array.isArray(date) && date.length >= 2) {
        return { dateFrom: new Date(date[0]), dateTo: new Date(date[1]) };
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    let dateFrom, dateTo;
    if (timePeriod === 'Za deň') {
        dateFrom = new Date(d); dateFrom.setHours(0, 0, 0, 0);
        dateTo = new Date(d); dateTo.setHours(23, 59, 59, 999);
    } else if (timePeriod === 'Za mesiac') {
        dateFrom = new Date(d.getFullYear(), d.getMonth(), 1);
        dateTo = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    } else if (timePeriod === 'Za rok') {
        dateFrom = new Date(d.getFullYear(), 0, 1);
        dateTo = new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);
    } else return null;
    return { dateFrom, dateTo };
}

export default function PreviewCal() {
    const { t, locale } = useContext(LocaleContext);
    const periodOptionLabels = useMemo(() => buildPreviewPeriodOptionLabels(t), [t]);
    const [calendarLocale, setCalendarLocale] = useState(() => registerPreviewCalendarLocale(addLocale, locale, t));
    useEffect(() => { setCalendarLocale(registerPreviewCalendarLocale(addLocale, locale, t)); }, [locale, t]);
    const [state, dispatch] = useContext(DatabaseContext);
    const { setUserMaskFilter } = useContext(UtilContext);

    const [timePeriod, setTimePeriod] = useState(PREVIEW_PERIOD_DEFAULT);
    const [date, setDate] = useState(null);
    const [dateProps, setDateProps] = useState({ hidden: true });
    const appliedSavedRef = useRef(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    

    const range = useMemo(() => getDateRange(timePeriod, date), [timePeriod, date]);

    const [statsFromApi, setStatsFromApi] = useState({ calibrated: [0,0,0,0], informative: [0,0,0,0], periodStats: null, certificates: [0,0,0,0] });
    const [statsLoading, setStatsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setStatsLoading(true);
        const params = {};
        if (range?.dateFrom && range?.dateTo) {
            params.date_from = toLocalDateString(range.dateFrom);
            params.date_to = toLocalDateString(range.dateTo);
        }
        const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
        getRequest('/api/gauges/preview_stats' + qs)
            .then((res) => {
                if (cancelled) return;
                const data = res?.data ?? res;
                setStatsFromApi({
                    calibrated: Array.isArray(data.calibrated) ? data.calibrated : [0,0,0,0],
                    informative: Array.isArray(data.informative) ? data.informative : [0,0,0,0],
                    periodStats: data.periodStats ?? null,
                    certificates: Array.isArray(data.certificates) ? data.certificates : [0,0,0,0],
                });
            })
            .catch(() => {
                if (cancelled) return;
                setStatsFromApi({ calibrated: [0,0,0,0], informative: [0,0,0,0], periodStats: null, certificates: [0,0,0,0] });
            })
            .finally(() => {
                if (!cancelled) setStatsLoading(false);
            });
        return () => { cancelled = true; };
    }, [range, refreshTrigger]);

    const periodStats = useMemo(() => {
        if (!range) return null;
        return statsFromApi.periodStats ?? null;
    }, [range, statsFromApi.periodStats]);

    const module_settings = state.module_settings || [];
    const COLOR_TO_HEX = { red: '#b91c1c', orange: '#ca8a04', green: '#15803d', gray: '#6b7280', blue: '#2563eb', black: '#171717' };
    const cal0 = getModuleSettingValue(module_settings, 'calibration', 'cal_graph_0', 'red');
    const cal15 = getModuleSettingValue(module_settings, 'calibration', 'cal_graph_15', 'orange');
    const cal30 = getModuleSettingValue(module_settings, 'calibration', 'cal_graph_30', 'orange');
    const calOk = getModuleSettingValue(module_settings, 'calibration', 'cal_graph_ok', 'green');
    const calOnTime = getModuleSettingValue(module_settings, 'calibration', 'cal_graph_on_time', 'green');
    const calLate = getModuleSettingValue(module_settings, 'calibration', 'cal_graph_late', 'red');
    const calibratedColors = useMemo(() => [
        COLOR_TO_HEX[cal0] || '#b91c1c',
        COLOR_TO_HEX[cal15] || '#ca8a04',
        COLOR_TO_HEX[cal30] || '#eab308',
        COLOR_TO_HEX[calOk] || '#15803d',
    ], [cal0, cal15, cal30, calOk]);
    const periodColors = useMemo(() => [
        COLOR_TO_HEX[calOnTime] || '#15803d',
        COLOR_TO_HEX[calLate] || '#b91c1c',
    ], [calOnTime, calLate]);

    const [calibratedData, setCalibratedData] = useState({
        labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Calibration.Fit')],
        datasets: [{ data: [0, 0, 0, 0], backgroundColor: calibratedColors, hoverBackgroundColor: calibratedColors }]
    });
    const [informativeData, setInformativeData] = useState({
        labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Calibration.Fit')],
        datasets: [{ data: [0, 0, 0, 0], backgroundColor: calibratedColors, hoverBackgroundColor: calibratedColors }]
    });
    const [certData, setCertData] = useState({
        labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Common.Status.OK')],
        datasets: [{ data: [0, 0, 0, 0], backgroundColor: calibratedColors, hoverBackgroundColor: calibratedColors }]
    });

    const displayCalibratedData = useMemo(() => {
        if (periodStats) {
            return {
                labels: [t('Calibration.DoneOnTime'), t('Calibration.DoneOverdue')],
                datasets: [{
                    data: [periodStats.calibratedOnTime ?? 0, periodStats.calibratedLate ?? 0],
                    backgroundColor: periodColors,
                    hoverBackgroundColor: periodColors,
                }],
            };
        }
        return { ...calibratedData, labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Calibration.Fit')], datasets: [{ ...calibratedData.datasets?.[0], backgroundColor: calibratedColors, hoverBackgroundColor: calibratedColors }] };
    }, [periodStats, calibratedData, calibratedColors, periodColors, t]);

    const displayInformativeData = useMemo(() => {
        if (periodStats) {
            return {
                labels: [t('Calibration.DoneOnTime'), t('Calibration.DoneOverdue')],
                datasets: [{
                    data: [periodStats.informativeOnTime ?? 0, periodStats.informativeLate ?? 0],
                    backgroundColor: periodColors,
                    hoverBackgroundColor: periodColors,
                }],
            };
        }
        return { ...informativeData, labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Calibration.Fit')], datasets: [{ ...informativeData.datasets?.[0], backgroundColor: calibratedColors, hoverBackgroundColor: calibratedColors }] };
    }, [periodStats, informativeData, calibratedColors, periodColors, t]);

    const displayCertData = useMemo(() => {
        if (periodStats) {
            return {
                labels: [t('Calibration.CertsUploadedOnTime'), t('Calibration.CertsUploadedOverdue')],
                datasets: [{
                    data: [periodStats.certsOnTime ?? 0, periodStats.certsLate ?? 0],
                    backgroundColor: periodColors,
                    hoverBackgroundColor: periodColors,
                }],
            };
        }
        return { ...certData, labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Common.Status.OK')], datasets: [{ ...certData.datasets?.[0], backgroundColor: calibratedColors, hoverBackgroundColor: calibratedColors }] };
    }, [periodStats, certData, calibratedColors, periodColors, t]);

    const periodStatsRef = useRef(null);
    const rangeRef = useRef(null);
    periodStatsRef.current = periodStats;
    rangeRef.current = range;

    const [calibratedOptions] = useState({
        plugins: { legend: { display: false } },
        onClick: function (evt, item) {
            if (!item || !item.length) return;
            if (periodStatsRef.current && rangeRef.current) {
                const seg = item[0].index === 0 ? 'on_time' : 'late';
                const r = rangeRef.current;
                const dateFrom = toLocalDateString(r.dateFrom);
                const dateTo = toLocalDateString(r.dateTo);
                dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { status: "Kalibrované", preview_period: { dateFrom, dateTo }, preview_segment: seg }, mask: 'calb015' });
                return;
            }
            if (item[0].index === 0) dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { preview: 'after', status: "Kalibrované" }, mask: 'calb015' });
            else if (item[0].index === 1) dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { preview: '15', status: "Kalibrované" }, mask: 'calb015' });
            else if (item[0].index === 2) dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { preview: '30', status: "Kalibrované" }, mask: 'calb015' });
            else if (item[0].index === 3) dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { preview: 'others', status: "Kalibrované" }, mask: 'calb015' });
        }
    });

    const [informativeOptions] = useState({
        plugins: { legend: { display: false } },
        onClick: function (evt, item) {
            if (!item || !item.length) return;
            if (periodStatsRef.current && rangeRef.current) {
                const seg = item[0].index === 0 ? 'on_time' : 'late';
                const r = rangeRef.current;
                const dateFrom = toLocalDateString(r.dateFrom);
                const dateTo = toLocalDateString(r.dateTo);
                dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { status: "Informatívne", preview_period: { dateFrom, dateTo }, preview_segment: seg }, mask: 'calb015' });
                return;
            }
            if (item[0].index === 0) dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { preview: 'after', status: "Informatívne" }, mask: 'calb015' });
            else if (item[0].index === 1) dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { preview: '15', status: "Informatívne" }, mask: 'calb015' });
            else if (item[0].index === 2) dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { preview: '30', status: "Informatívne" }, mask: 'calb015' });
            else if (item[0].index === 3) dispatch({ type: 'create_new_tab', name: t('Calibration.ListByState'), translationKey: 'Calibration.ListByState', legacyLabel: t('Calibration.ListByState'), page: 'CalibrationStateList', props: { preview: 'others', status: "Informatívne" }, mask: 'calb015' });
        }
    });

    const [certOptions] = useState({
        plugins: { legend: { display: false } },
        onClick: function (evt, item) {
            if (!item || !item.length) return;
            if (periodStatsRef.current && rangeRef.current) {
                const seg = item[0].index === 0 ? 'on_time' : 'late';
                const r = rangeRef.current;
                const dateFrom = toLocalDateString(r.dateFrom);
                const dateTo = toLocalDateString(r.dateTo);
                dispatch({ type: 'create_new_tab', name: t('Calibration.CertsByState'), translationKey: 'Calibration.CertsByState', legacyLabel: t('Calibration.CertsByState'), page: 'CalibrationCompanyCertStateList', props: { preview_period: { dateFrom, dateTo }, preview_segment: seg }, mask: 'calb013' });
                return;
            }
            dispatch({ type: 'create_new_tab', name: t('Calibration.CertsByState'), translationKey: 'Calibration.CertsByState', legacyLabel: t('Calibration.CertsByState'), page: 'CalibrationCompanyCertStateList', props: {}, mask: 'calb013' });
        }
    });

    const plugin = {
        id: 'emptyDoughnut',
        afterDraw(chart) {
            const data = chart.data?.datasets?.[0]?.data ?? [];
            const count = data.reduce((a, b) => a + (b ?? 0), 0);
            if (count < 1) {
                const ctx = chart.ctx;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.font = '20px Arial';
                ctx.fillStyle = '#9ca3af';
                ctx.fillText(t('Common.NoRecords'), chart.width / 2, chart.height / 2);
            }
        },
    };

    const [legendHidden, setLegendHidden] = useState({ calibrated: [], informative: [], cert: [] });
    const originalDataRef = useRef({ calibrated: [], informative: [], cert: [] });
    const originalTotal = (key, fallbackArr) => {
        const base = (fallbackArr && fallbackArr.length) ? fallbackArr : originalDataRef.current[key];
        const src = base ?? [];
        return src.reduce((a, b) => a + (Number(b) || 0), 0);
    };
    const calibratedChartRef = useRef(null);
    const informativeChartRef = useRef(null);
    const certChartRef = useRef(null);

    const toggleLegendItem = (chartKey, index, chartRef) => () => {
        const ch = chartRef?.current?.getChart?.();
        if (!ch) return;
        const originals = originalDataRef.current[chartKey];
        if (!Array.isArray(originals)) return;
        const currentlyHidden = legendHidden[chartKey]?.[index];
        const nextHidden = !currentlyHidden;
        const hiddenArr = legendHidden[chartKey] ?? [];
        const newData = originals.map((val, i) => {
            const isHidden = i === index ? nextHidden : hiddenArr[i];
            return isHidden ? 0 : (Number(val) || 0);
        });
        const ds = ch.data.datasets[0];
        ds.data = [...newData];
        const meta = ch.getDatasetMeta?.(0);
        if (meta && Array.isArray(meta._parsed)) newData.forEach((v, i) => { meta._parsed[i] = v; });
        ch.update();
        setLegendHidden((prev) => {
            const arr = prev[chartKey] ?? [];
            const next = [...arr];
            next[index] = nextHidden;
            return { ...prev, [chartKey]: next };
        });
    };

    const handleRefresh = () => setRefreshTrigger((t) => t + 1);

    useEffect(() => {
        if (appliedSavedRef.current) return;
        if (!state.user_mask_filters?.length) return;
        appliedSavedRef.current = true;
        const mask = (state.user_mask_filters || []).find((m) => m.page === PREVIEW_PAGE);
        const attrs = mask?.attributes;
        const parsed = typeof attrs === 'string' ? (() => { try { return parseSavedPeriod(JSON.parse(attrs)); } catch { return null; } })() : parseSavedPeriod(attrs);
        if (parsed) {
            setTimePeriod(parsed.timePeriod);
            setDate(parsed.date ?? defaultDateForPeriod(parsed.timePeriod));
            setDateProps(datePickerPropsForPeriod(parsed.timePeriod));
        }
    }, [state.user_mask_filters]);

    function changeTimePeriod(time_period) {
        setTimePeriod(time_period);
        setDate(defaultDateForPeriod(time_period));
        setDateProps(datePickerPropsForPeriod(time_period));
        setStatsLoading(true);
    }

    function showResult(dateVal) {
        setStatsLoading(true);
        setDate(dateVal);
    }

    const savePeriodToDb = () => {
        setUserMaskFilter(PREVIEW_PAGE, 'set_attributes', null, null, { preview_period: serializePeriod(timePeriod, date) });
    };

    useEffect(() => {
        setCalibratedData({
            labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Calibration.Fit')],
            datasets: [{
                data: [...(statsFromApi.calibrated || [0, 0, 0, 0])],
                backgroundColor: calibratedColors,
                hoverBackgroundColor: calibratedColors,
            }]
        });
        setInformativeData({
            labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Calibration.Fit')],
            datasets: [{
                data: [...(statsFromApi.informative || [0, 0, 0, 0])],
                backgroundColor: calibratedColors,
                hoverBackgroundColor: calibratedColors,
            }]
        });
        const cert = statsFromApi.certificates || [0, 0, 0, 0];
        setCertData({
            labels: [t('Common.Expiry.After'), t('Common.Expiry.Days15Short'), t('Common.Expiry.Days30Short'), t('Common.Status.OK')],
            datasets: [{
                data: [cert[0] ?? 0, cert[1] ?? 0, cert[2] ?? 0, cert[3] ?? 0],
                backgroundColor: calibratedColors,
                hoverBackgroundColor: calibratedColors,
            }]
        });
    }, [statsFromApi.calibrated, statsFromApi.informative, statsFromApi.certificates, calibratedColors]);

    useEffect(() => {
        const d = (src) => (src?.datasets?.[0]?.data ? [...src.datasets[0].data] : []);
        originalDataRef.current = {
            calibrated: d(displayCalibratedData),
            informative: d(displayInformativeData),
            cert: d(displayCertData),
        };
    }, [displayCalibratedData, displayInformativeData, displayCertData]);

    const ChartLegend = ({ labels, colors, chartKey, chartRef, hiddenArr = [] }) => {
        const isClickable = chartKey != null && chartRef != null && !periodStats;
        return (
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                {labels?.map((label, i) =>
                    isClickable ? (
                        <button
                            key={i}
                            type="button"
                            onClick={toggleLegendItem(chartKey, i, chartRef)}
                            className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                            style={{ textDecoration: hiddenArr[i] ? 'line-through' : undefined, opacity: hiddenArr[i] ? 0.5 : 1 }}
                        >
                            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colors?.[i] ?? '#6b7280' }} />
                            {label}
                        </button>
                    ) : (
                        <span key={i} className="inline-flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colors?.[i] ?? '#6b7280' }} />
                            {label}
                        </span>
                    )
                )}
            </div>
        );
    };

    return (
        <div className="min-h-[calc(100vh-137px)]">
            <div className="lg:grid grid-cols-3 gap-1">
                <div></div>
                <div>
                    <div className="flex flex-wrap items-center gap-3 mt-4 px-4">
                        <label htmlFor="time_period" className="text-xl font-semibold text-gray-800 shrink-0">{t('Common.PeriodLabel')}</label>
                        <select
                            name="time_period"
                            id="time_period"
                            value={timePeriod}
                            onChange={(e) => changeTimePeriod(e.target.value)}
                            className="bg-white flex-1 min-w-0 max-w-[180px] px-4 py-2 text-gray-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        >
                            <option value="Všetko">{periodOptionLabels['Všetko']}</option>
                            <option value="Za deň">{periodOptionLabels['Za deň']}</option>
                            <option value="Za mesiac">{periodOptionLabels['Za mesiac']}</option>
                            <option value="Za rok">{periodOptionLabels['Za rok']}</option>
                            <option value="Vlastné">{periodOptionLabels['Vlastné']}</option>
                        </select>
                        <button
                            type="button"
                            onClick={handleRefresh}
                            title={t('Common.RefreshData')}
                            className="flex items-center justify-center w-10 h-10 shrink-0 rounded-md border border-gray-300 bg-gray-600 hover:bg-gray-700 text-white transition-colors"
                        >
                            <RefreshIcon />
                        </button>
                        <button
                            type="button"
                            onClick={savePeriodToDb}
                            title={t('Common.SaveSelectedPeriod')}
                            className="shrink-0 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-gray-200"
                        >
                            {t('Common.SaveMask')}
                        </button>
                    </div>
                </div>
                <div></div>
                <div></div>
                <div className="flex-auto px-4">
                    <Calendar
                        id="previewcal_date"
                        style={{ border: '1px solid #e5e7eb', borderRadius: '6px' }}
                        value={date}
                        onChange={(e) => showResult(e.target.value)}
                        showIcon
                        className="w-full"
                        visible={true}
                        locale={calendarLocale}
                        {...dateProps}
                        hideOnRangeSelection
                    />
                </div>
                <div></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
                <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                    <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewCal.CalibratedGauges')}</h2>
                    <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
                        {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : <Chart ref={calibratedChartRef} type="doughnut" data={displayCalibratedData} options={calibratedOptions} className="w-full h-full" plugins={[plugin]} />}
                    </div>
                    {!statsLoading && <ChartLegend labels={displayCalibratedData.labels} colors={displayCalibratedData.datasets?.[0]?.backgroundColor} chartKey="calibrated" chartRef={calibratedChartRef} hiddenArr={legendHidden.calibrated} />}
                    <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : t('Common.TotalCount', { count: originalTotal('calibrated', displayCalibratedData?.datasets?.[0]?.data) })}</div>
                </div>
                <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                    <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewCal.InformativeGauges')}</h2>
                    <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
                        {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : <Chart ref={informativeChartRef} type="doughnut" data={displayInformativeData} options={informativeOptions} className="w-full h-full" plugins={[plugin]} />}
                    </div>
                    {!statsLoading && <ChartLegend labels={displayInformativeData.labels} colors={displayInformativeData.datasets?.[0]?.backgroundColor} chartKey="informative" chartRef={informativeChartRef} hiddenArr={legendHidden.informative} />}
                    <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : t('Common.TotalCount', { count: originalTotal('informative', displayInformativeData?.datasets?.[0]?.data) })}</div>
                </div>
                <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                    <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewCal.CalibrationCompanyCertificates')}</h2>
                    <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
                        {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : <Chart ref={certChartRef} type="doughnut" data={displayCertData} options={certOptions} className="w-full h-full" plugins={[plugin]} />}
                    </div>
                    {!statsLoading && <ChartLegend labels={displayCertData.labels} colors={displayCertData.datasets?.[0]?.backgroundColor} chartKey="cert" chartRef={certChartRef} hiddenArr={legendHidden.cert} />}
                    <div className="text-center font-semibold mt-4">{statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : t('Common.TotalCount', { count: originalTotal('cert', displayCertData?.datasets?.[0]?.data) })}</div>
                </div>
            </div>
        </div>
    );
}
